import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import feneroLogo from '../assets/logo-orange.svg'

// Responsive background component

// Robust base path for Vite public/ assets
const BASE = import.meta.env.BASE_URL || '/'
// Folder where *your* optimized files actually live:
const IMG = `${BASE}assets/login-page/`   // <-- Fixed path to include assets/

const BgPicture: React.FC = () => (
  <div className="absolute inset-0">
    <picture className="block h-full w-full">
      {/* WebP first */}
      <source
        type="image/webp"
        srcSet={[
          `${IMG}optimized_background_small.webp 500w`,
          `${IMG}optimized_background_medium.webp 1000w`,
          `${IMG}optimized_background_large.webp 1920w`
        ].join(', ')}
        sizes="(min-width: 1024px) 50vw, 100vw"
      />
      {/* JPG fallback */}
      <img
        src={`${IMG}optimized_background_large.jpg`}
        srcSet={[
          `${IMG}optimized_background_small.jpg 500w`,
          `${IMG}optimized_background_medium.jpg 1000w`,
          `${IMG}optimized_background_large.jpg 1920w`
        ].join(', ')}
        sizes="(min-width: 1024px) 50vw, 100vw"
        alt="Resort balcony overlooking lawn and palm trees"
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="h-full w-full object-cover"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.1), rgba(0,0,0,.1))' }}
        onError={(e) => {
          console.warn('Background image failed to load', (e.target as HTMLImageElement).src)
        }}
      />
    </picture>
  </div>
)

const LoginPage: React.FC = () => {
  const { sendMagicLink, signInWithMagicLink, user, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [error, setError] = useState('')

  // Check if user is already authenticated
  useEffect(() => {
    if (user && !loading) {
      const from = location.state?.from?.pathname || '/quick-check'
      navigate(from, { replace: true })
    }
  }, [user, loading, navigate, location])

  // Check for magic link on component mount
  useEffect(() => {
    const checkMagicLink = async () => {
      const savedEmail = localStorage.getItem('emailForSignIn')
      if (savedEmail && window.location.href.includes('apiKey')) {
        try {
          await signInWithMagicLink(savedEmail)
          setEmailSent(true)
        } catch (error) {
          console.error('Magic link sign-in failed:', error)
          setError('Invalid or expired magic link. Please try again.')
        }
      }
    }
    
    // Only run if we're not already loading or have a user
    if (!loading && !user) {
      checkMagicLink()
    }
  }, [signInWithMagicLink, loading, user])

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If already authenticated, redirect to app
  if (user) {
    const from = (location.state as any)?.from?.pathname || '/quick-check'
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Please enter your email address')
      return
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Use and Privacy Policy')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      await sendMagicLink(email)
      setEmailSent(true)
    } catch (err) {
      console.error('Failed to send magic link:', err)
      setError('Failed to send magic link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // const handleGoogleLogin = () => {
  //   console.log('Google login clicked')
  // }

  if (emailSent) {
    return (
      <div className="min-h-screen flex">
        {/* Background image - desktop only */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <BgPicture />
          {/* Logo */}
          <div className="absolute top-8 left-8 flex items-center space-x-2">
            <img src={feneroLogo} alt="Fenero logo" className="h-8 brightness-0 invert" />
            <span className="text-white text-xl font-bold">Fenero</span>
          </div>
        </div>

        {/* Right side - Success message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 flex items-center space-x-2">
              <img src={feneroLogo} alt="Fenero logo" className="h-8" />
              <span className="text-gray-900 text-xl font-bold">Fenero</span>
            </div>
            <div className="text-center">
              <svg
                className="mx-auto h-16 w-16 text-green-500 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Check your email!</h2>
              <p className="text-gray-600 mb-8">
                We've sent a magic link to <strong>{email}</strong>. 
                Click the link in the email to sign in to your account.
              </p>
              <button
                onClick={() => setEmailSent(false)}
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Didn't receive the email? Try again
              </button>
            </div>
        </div>
      </div>

      {/* Mobile background and success overlay - shows on small screens */}
      <div className="lg:hidden absolute inset-0">
        {/* Mobile background image */}
        <BgPicture />
        {/* Mobile logo */}
        <div className="absolute top-8 left-8 flex items-center space-x-2">
          <img src={feneroLogo} alt="Fenero logo" className="h-8 brightness-0 invert" />
          <span className="text-white text-xl font-bold">Fenero</span>
        </div>
        
        {/* Mobile success overlay */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white/90 backdrop-blur-sm p-8 border border-gray-200 rounded-lg shadow-lg text-center">
              <svg
                className="mx-auto h-16 w-16 text-green-500 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Check your email!</h2>
              <p className="text-gray-600 mb-8">
                We've sent a magic link to <strong>{email}</strong>. 
                Click the link in the email to sign in to your account. The link will expire in 1 hour.
              </p>
              <button
                onClick={() => setEmailSent(false)}
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Didn't receive the email? Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
  }

  return (
    <div className="min-h-screen flex">
      {/* Background image - desktop only */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <BgPicture />
        {/* Logo */}
        <div className="absolute top-8 left-8 flex items-center space-x-2">
          <img src={feneroLogo} alt="Fenero logo" className="h-8 brightness-0 invert" />
          <span className="text-white text-xl font-bold">Fenero</span>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full p-4 sm:p-8">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 flex items-center space-x-2">
            <img src={feneroLogo} alt="Fenero logo" className="h-8" />
            <span className="text-gray-900 text-xl font-bold">Fenero</span>
          </div>
          <div>
            <div className="p-8 border border-gray-200 rounded-lg shadow-sm">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign In to your account</h1>
              <p className="text-gray-600 mb-8">
                Access your personal dashboard to initiate income verification, analyze bank statements, and process loans using AI.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                    placeholder="Enter your email address"
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-2 w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-600">
                      By signing in, I agree to our{' '}
                      <a href="https://fenero.ai/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">Terms of use</a>{' '}
                      and{' '}
                      <a href="https://fenero.ai/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">Privacy Policy</a>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending magic link...
                    </span>
                  ) : (
                    'Sign In with Magic Link'
                  )}
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  {/* <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div> */}
                  {/* <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">OR</span>
                  </div> */}
                </div>

                {/* <button
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="mt-6 w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-4 px-4 rounded-full transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile background and overlay - shows on small screens */}
      <div className="lg:hidden absolute inset-0">
        {/* Mobile background image */}
        <BgPicture />
        {/* Mobile logo */}
        <div className="absolute top-8 left-8 flex items-center space-x-2">
          <img src={feneroLogo} alt="Fenero logo" className="h-8 brightness-0 invert" />
          <span className="text-white text-xl font-bold">Fenero</span>
        </div>
        
        {/* Mobile overlay form */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white/90 backdrop-blur-sm p-8 border border-gray-200 rounded-lg shadow-lg">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign In to your account</h1>
              <p className="text-gray-600 mb-8">
                Access your personal dashboard to initiate income verification, analyze bank statements, and process loans using AI.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="email-mobile" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email-mobile"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                    placeholder="Enter your email address"
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-2 w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-600">
                      By creating an account, I agree to our{' '}
                      <a href="https://fenero.ai/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">Terms of use</a>{' '}
                      and{' '}
                      <a href="https://fenero.ai/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">Privacy Policy</a>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending magic link...
                    </span>
                  ) : (
                    'Sign In with Magic Link'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default LoginPage
